import * as React from "react";
import { useCpFormContext } from "../../../../components/form/useCpFormContext";
import { useCpFormValue, hasNestedPath } from "../../../gvc/testNewForm/utils";
import { useAppSelector } from "../../../../store/hooks";
import {
  validateContainerResources,
  getResourceFieldPaths,
  ResourceValidationResult,
  ResourceSuggestion,
} from "../utils/resourceValidation";

export type { ResourceValidationResult, ResourceSuggestion };

interface UseResourceValidationsResult {
  validations: ResourceValidationResult[];
  hasErrors: boolean;
}

/**
 * Hook that checks for resource validation errors on a container.
 * This hook acts as a proxy - it first checks if there are any errors
 * in the form state for resource fields, then calls the validation
 * function to get detailed info with suggestions.
 */
export function useResourceValidations(containerIndex: number): UseResourceValidationsResult {
  const { id } = useCpFormContext();

  // Get form errors from the correct location in state (not from values)
  const errors = useAppSelector((state) => state.form.instances[id]?.errors) || {};

  // Get all the values needed for validation
  const type = useCpFormValue(`type`);
  const tags = useCpFormValue(`tags`) || [];
  const capacityAI = useCpFormValue(`defaultOptions.capacityAI`);

  const gpu = useCpFormValue(`containers.${containerIndex}.gpu`);
  const gpuType = gpu?.type;
  const gpuNvidiaModel = gpu?.nvidia?.model;
  const gpuNvidiaQuantity = gpu?.nvidia?.quantity;

  const minCpuAmount = useCpFormValue(`containers.${containerIndex}.minCpu.amount`);
  const minCpuUnit = useCpFormValue(`containers.${containerIndex}.minCpu.unit`);
  const maxCpuAmount = useCpFormValue(`containers.${containerIndex}.cpu.amount`);
  const maxCpuUnit = useCpFormValue(`containers.${containerIndex}.cpu.unit`);

  const minMemoryAmount = useCpFormValue(`containers.${containerIndex}.minMemory.amount`);
  const minMemoryUnit = useCpFormValue(`containers.${containerIndex}.minMemory.unit`);
  const maxMemoryAmount = useCpFormValue(`containers.${containerIndex}.memory.amount`);
  const maxMemoryUnit = useCpFormValue(`containers.${containerIndex}.memory.unit`);

  const hasRelaxedRatioTag = tags.some?.((t: any) => t.name === "cpln/relaxMemoryToCpuRatio") || false;

  // Check if any resource field has an error (errors are stored in nested structure)
  const resourceFieldPaths = getResourceFieldPaths(containerIndex);
  const hasErrors = resourceFieldPaths.some((path) => hasNestedPath(errors, path));

  // Only compute validations if there are errors
  const validations = React.useMemo(() => {
    if (!hasErrors) {
      return [];
    }

    return validateContainerResources({
      type,
      capacityAI,
      hasRelaxedRatioTag,
      minCpuAmount,
      minCpuUnit,
      maxCpuAmount,
      maxCpuUnit,
      minMemoryAmount,
      minMemoryUnit,
      maxMemoryAmount,
      maxMemoryUnit,
      gpuType,
      gpuNvidiaModel,
      gpuNvidiaQuantity,
    });
  }, [
    hasErrors,
    type,
    capacityAI,
    hasRelaxedRatioTag,
    minCpuAmount,
    minCpuUnit,
    maxCpuAmount,
    maxCpuUnit,
    minMemoryAmount,
    minMemoryUnit,
    maxMemoryAmount,
    maxMemoryUnit,
    gpuType,
    gpuNvidiaModel,
    gpuNvidiaQuantity,
  ]);

  return {
    validations,
    hasErrors,
  };
}
